import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  Card,
  CardMedia,
  CardContent,
  Box,
  makeStyles
} from '@material-ui/core';
import { ArrowForward } from '@material-ui/icons';
import { colorSet, clsx } from '@components/Provider';
import { CustomizedTypography } from '@components/Customized';
import useRequest from '@hooks/useRequest';
import useQueryString from '@hooks/useQueryString';
import useUserActionLog, {
  Enum_Action_Button_Enum
} from '@hooks/useUserActionLog';
import {
  CapaActivityType,
  CapaPcbServiceType,
  CapaQuotationDetailedServiceType,
  CapaQuotationServiceType
} from '@generated/graphql';

export interface ProcessmethodCardProps {
  img?: string;
  label?: string;
  english?: string;
  serviceType: CapaQuotationServiceType;
  detailedServiceType: CapaQuotationDetailedServiceType | CapaPcbServiceType;
  reccomendation?: boolean;
  log?: Enum_Action_Button_Enum;
  onHasRequestDialogOpen?: () => void;
  setDetailedServiceType?: (service: CapaQuotationDetailedServiceType) => void;
}

const ProcessmethodCard: React.FC<ProcessmethodCardProps> = ({
  img,
  label,
  english,
  serviceType,
  detailedServiceType,
  reccomendation = false,
  log,
  onHasRequestDialogOpen,
  setDetailedServiceType
}) => {
  const {
    cardContainer,
    cardMedia,
    cardContent,
    reccomendationCardContainer
  } = useStyles();
  const { getQueryString } = useQueryString();
  const { hasNotEndedRequest } = useRequest();
  const history = useHistory();
  const { insertButtonLog, onInsertUserActionLogOne } = useUserActionLog();

  const onSendRequest = () => {
    if (
      hasNotEndedRequest &&
      onHasRequestDialogOpen &&
      setDetailedServiceType
    ) {
      setDetailedServiceType(
        detailedServiceType as CapaQuotationDetailedServiceType
      );
      onHasRequestDialogOpen();
      return;
    }

    history.push({
      pathname: '/request',
      search: getQueryString({
        serviceType: serviceType,
        detailedServiceType: detailedServiceType
      }),
      state: {
        fromLandingPage: true
      }
    });
  };

  return (
    <Card
      className={clsx([
        cardContainer,
        reccomendation ? reccomendationCardContainer : ''
      ])}
      onClick={() => {
        if (reccomendation) {
          insertButtonLog(
            `Button_Landing_ServiceTypeCard_${serviceType}_Ref` as keyof typeof Enum_Action_Button_Enum
          );
        }

        if (log) {
          onInsertUserActionLogOne(log, CapaActivityType.ButtonClick);
        }
        onSendRequest();
      }}
    >
      {!reccomendation && (
        <>
          <CardContent className={cardContent}>
            <CustomizedTypography
              variant={'body2'}
              fontWeight="500"
              color="textPrimary"
            >
              <i className="request-gtm-detailServiceType-btn">{label}</i>
            </CustomizedTypography>
            {english && (
              <CustomizedTypography variant={'subtitle2'} color="textSecondary">
                ({english})
              </CustomizedTypography>
            )}
          </CardContent>
          <CardMedia className={cardMedia} image={img} />
        </>
      )}
      {reccomendation && (
        <Box className="reccomendationBox">
          <CustomizedTypography
            variant={'subtitle2'}
            fontWeight="500"
            color="textPrimary"
            className="reccomendationText"
          >
            파트너 추천방식으로 <br />
            견적 요청하기
          </CustomizedTypography>
          <ArrowForward />
        </Box>
      )}
    </Card>
  );
};

export default ProcessmethodCard;

const useStyles = makeStyles((theme) => ({
  cardContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    maxWidth: 241,
    width: 241,
    minHeight: 72,
    padding: '12px 16px',
    borderRadius: 12,
    boxShadow: 'unset',
    border: `1px solid ${colorSet.gray400}`,
    '&:hover': {
      backgroundColor: colorSet.gray100,
      cursor: 'pointer',
      '& .requestNow': {
        color: colorSet.primary500
      }
    },
    transition: '0.3s',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
      width: '100%'
    }
  },
  cardMedia: {
    minWidth: 48,
    maxHeight: 48,
    borderRadius: 8
  },
  cardContent: {
    padding: 0,
    paddingTop: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    '& button': {
      '& span': {
        justifyContent: 'flex-start'
      },
      '&.text:hover': {
        backgroundColor: colorSet.gray400
      }
    },
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center'
    }
  },
  reccomendationCardContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colorSet.gray100,
    border: 'none',
    '& .reccomendationBox': {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    '&:hover': {
      '& svg': {
        fill: colorSet.primary500
      }
    }
  }
}));
